import { useMemo } from 'react';
import { GLPeriods } from 'src/services/useGLPeriodApi';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import { PoDetailsObject } from 'src/services/usePoDetailsApi';
import { round } from 'src/utils/number';
import { IInvoiceBudgetPeriod } from '../invoice_details/InvoiceDetailsContext';

export interface BudgetLineItemError {
  period: string[];
  percentageAmount: string[];
  amount: string[];
  periodStatus: string[];
}

export interface BudgetLineItemInfo {
  glPeriod: string[];
  amount: string[];
  percentageAmount: string[];
  periodStatus: string[];
}

export interface poAllocations {
  glPeriodId: number;
  amount: number;
  percentageAmount: number;
  isClosed: number;
}

const defaultViewBudgetErrors: BudgetLineItemError = {
  period: [],
  percentageAmount: [],
  amount: [],
  periodStatus: [],
};

export const defaultViewBudgetInfos: BudgetLineItemInfo = {
  glPeriod: [],
  amount: [],
  percentageAmount: [],
  periodStatus: [],
};

export const periodNotSet = 'This period is not set on the PO';
export const percentageLess = 'PO % allocation for this period is less than Invoice % allocation';
export const amountLess = 'PO $ allocation for this period is less than Invoice $ allocation';
export const percentageGreater =
  'PO % allocation for this period is greater than Invoice % allocation';
export const amountGreater = 'PO $ allocation for this period is greater than Invoice $ allocation';

const useViewInvoiceBudgetTableErrors = (
  type: 'Percentage' | 'Amount',
  grandTotalAmt: number,
  glPeriods: GLPeriods,
  isInvoiceEditDisabled: boolean,
  poData: PoDetailsObject,
  invoiceBudgetPeriods: IInvoiceBudgetPeriod[],
  invoice: InvoiceDetailsResponse
) => {
  const {
    errors = [],
    saveErrors = [],
    infos = [],
  } = useMemo(() => {
    const errors = invoiceBudgetPeriods?.map((item) => {
      let error = { ...defaultViewBudgetErrors };
      let saveError = { ...defaultViewBudgetErrors };
      if (!isInvoiceEditDisabled) {
        if (!item?.glPeriodId) {
          error.period = ['Please select an accounting period'];
          saveError.period = ['Please assign an accounting period to all rows'];
        }

        if (round(item.percentageAmount, 2) === 0 && type === 'Percentage') {
          error.percentageAmount = ['Please enter a positive % Distribution value'];
          saveError.percentageAmount = ['Please enter positive % Distribution value(s)'];
        }

        if (round(item.amount, 2) === 0 && type === 'Amount') {
          error.amount = [
            `Please enter a ${
              round(grandTotalAmt, 2) >= 0 ? 'positive' : 'negative'
            } $ Distribution value`,
          ];

          saveError.percentageAmount = [
            `Please enter ${
              round(grandTotalAmt, 2) >= 0 ? 'positive' : 'negative'
            } $ Distribution value(s)`,
          ];
        }

        if (glPeriods.find((period) => period.glPeriodId === item.glPeriodId)?.isClosed) {
          error.periodStatus = ['Document includes a closed period.'];
          saveError.periodStatus = ['Document includes a closed period.'];
        }
      }
      return { error, saveError };
    }, []);

    let infos = [{ ...defaultViewBudgetInfos }];
    if (invoiceBudgetPeriods?.length === 1 && poData?.allocations?.length === 0) {
      const isSinglePeriodAllocationSameAsPo =
        poData?.glPeriodId === invoiceBudgetPeriods?.[0]?.glPeriodId;
      const hasValidGlPeriodId = !!invoiceBudgetPeriods[0]?.glPeriodId;
      const glPeriodWarningCondition = hasValidGlPeriodId && !isSinglePeriodAllocationSameAsPo;
      const amountLessWarningCondition =
        round(Number(poData?.poTotal), 2) < round(Number(invoice.totalCostAmt), 2);
      const amountGreaterCondition =
        round(Number(poData?.poTotal), 2) > round(Number(invoice.totalCostAmt), 2);

      const periodPercentageAmount = round(invoiceBudgetPeriods[0].percentageAmount, 2);
      const percentageLessCondition = periodPercentageAmount > 100;
      const percentageCondition = periodPercentageAmount < 100;

      infos = [
        {
          ...defaultViewBudgetInfos,
          ...(glPeriodWarningCondition
            ? {
                glPeriod: [periodNotSet],
              }
            : hasValidGlPeriodId
            ? {
                ...(amountLessWarningCondition ? { amount: [amountLess] } : {}),
                ...(amountGreaterCondition ? { amount: [amountGreater] } : {}),
                ...(percentageLessCondition ? { percentageAmount: [percentageLess] } : {}),
                ...(percentageCondition ? { percentageAmount: [percentageGreater] } : {}),
              }
            : {}),
        } as BudgetLineItemInfo,
      ];
    }

    if (invoiceBudgetPeriods?.length === 1 && poData?.allocations?.length) {
      const poAllocation = poData?.allocations?.find(
        (poAllocation) => poAllocation?.glPeriodId === invoiceBudgetPeriods?.[0]?.glPeriodId
      );
      const hasValidGlPeriodId = !!invoiceBudgetPeriods[0]?.glPeriodId;
      const glPeriodWarningCondition = hasValidGlPeriodId && !poAllocation;
      const amountLessWarningCondition =
        round(Number(poAllocation?.amount), 2) < round(Number(invoice.totalCostAmt), 2);
      const amountGreaterWarningCondition =
        round(Number(poAllocation?.amount), 2) > round(Number(invoice.totalCostAmt), 2);
      const percentageLessWarningCondition =
        round(Number(poAllocation?.percentageAmount), 2) >
        round(Number(invoiceBudgetPeriods[0].percentageAmount));
      const percentageGreaterWarningCondition =
        round(Number(poAllocation?.percentageAmount), 2) <
        round(Number(invoiceBudgetPeriods[0].percentageAmount));

      infos = [
        {
          ...defaultViewBudgetInfos,
          ...(glPeriodWarningCondition
            ? {
                glPeriod: [periodNotSet],
              }
            : hasValidGlPeriodId
            ? {
                ...(amountLessWarningCondition ? { amount: [amountLess] } : {}),
                ...(amountGreaterWarningCondition ? { amount: [amountGreater] } : {}),
                ...(percentageLessWarningCondition ? { percentageAmount: [percentageLess] } : {}),
                ...(percentageGreaterWarningCondition
                  ? { percentageAmount: [percentageGreater] }
                  : {}),
              }
            : {}),
        },
      ];
    }

    infos = invoiceBudgetPeriods?.map((item) => {
      const poPeriodAllocation = poData?.allocations?.find(
        (detail) => detail.glPeriodId === item.glPeriodId
      );
      let warning = { ...defaultViewBudgetInfos };

      if (poData?.allocations?.length === 0) {
        if (item.glPeriodId && poData?.glPeriodId !== item?.glPeriodId) {
          warning.glPeriod = [periodNotSet];
        } else if (item.glPeriodId) {
          if (round(poData?.poTotal, 2) < round(item.amount, 2)) {
            warning.amount = [amountLess];
          }
          if (round(poData?.poTotal, 2) > round(item.amount, 2)) {
            warning.amount = [amountGreater];
          }
          if (100 > round(item.percentageAmount, 2)) {
            warning.percentageAmount = [percentageGreater];
          }
          if (100 < round(item.percentageAmount, 2)) {
            warning.percentageAmount = [percentageLess];
          }
        }
      } else {
        if (!poPeriodAllocation) {
          if (item.glPeriodId && poData?.glPeriodId !== item?.glPeriodId) {
            warning.glPeriod = [periodNotSet];
          }
        } else {
          if (round(poPeriodAllocation?.amount, 2) < round(item.amount, 2)) {
            warning.amount = [amountLess];
          }
          if (round(poPeriodAllocation?.amount, 2) > round(item.amount, 2)) {
            warning.amount = [amountGreater];
          }
          if (round(poPeriodAllocation?.percentageAmount, 2) > round(item.percentageAmount, 2)) {
            warning.percentageAmount = [percentageGreater];
          } else if (
            round(poPeriodAllocation?.percentageAmount, 2) < round(item.percentageAmount, 2)
          ) {
            warning.percentageAmount = [percentageLess];
          }
        }
      }

      return warning;
    });

    return {
      errors: errors?.map((err) => err.error) || [],
      saveErrors: errors?.map((err) => err.saveError) || [],
      infos,
    };
  }, [
    glPeriods,
    grandTotalAmt,
    invoice.totalCostAmt,
    invoiceBudgetPeriods,
    isInvoiceEditDisabled,
    poData?.allocations,
    poData?.glPeriodId,
    poData?.poTotal,
    type,
  ]);

  return {
    errors,
    saveErrors,
    infos,
  };
};

export default useViewInvoiceBudgetTableErrors;
